import { runInAction, computed, action, observable } from 'mobx'
import Document from 'domain/Document'
import { Store } from 'libx'
import { makeMomentSorter } from 'utils/arrayUtil'
import moment from 'moment'
import memoize from 'memoizee'
import autobind from 'autobind-decorator'
import { FlashMessageTypes } from 'components/FlashMessages/FlashMessageStore'

const sortByDateUpdated = makeMomentSorter('desc', (x) => x.dateUpdated)

@autobind
class DocumentStore extends Store {
  @observable stagedDocuments = []
  @observable loading = false

  constructor ({ storage }) {
    super(...arguments)
    this.documents = this.collection({
      model: Document,
      getModelId: (doc) => doc.id,
      idAttribute: 'Key' // on the source (AWS result)
    })

    this.rootStore.authStore.onLogout(action(() => this.documents.clear()))
    this.fetchDocuments = memoize(this.fetchDocuments, { promise: true })
  }

  @computed get allDocuments () {
    return [
      ...this.stagedDocuments.slice(),
      ...this.documents.map(x => x).sort(sortByDateUpdated)
    ]
  }

  documentsInCategory (category) {
    const all = this.allDocuments
    return all.filter(x => x.category === category)
  }

  async update (document, data) {
    const result = await this.rootStore.api.documents.patch(document.id, {
      meta: {
        name: data.label,
        year: data.year,
        type: data.type,
        category: data.category,
        notes: data.description || ''
      }
    })
    runInAction(() => {
      document.set({
        ...data,
        dateUpdated: moment(Date.parse(result.LastModified))
      })
    })
  }

  @action stageFiles (category, files, jobId) {
    files = Array.from(files)
    const documents = files.map((f) =>
      new Document({
        category,
        label: f.name,
        fileObject: f,
        uploaded: false,
        jobId,
        uploadedBy: this.rootStore.authStore.user
      }, {
        rootStore: this.rootStore
      })
    )

    documents.forEach(d => d.editor.startEditing())
    this.stagedDocuments.unshift(...documents)
  }

  @action save (document, attrs) {
    if (document.isNew) {
      document.set(attrs)
      this.upload(document)
      return
    }

    // Update takes care of setting.
    // Returning here will defer closing the
    // editor until the document has been saved.
    return this.update(document, attrs)
  }

  @action removeStagedDocument (document) {
    this.stagedDocuments.remove(document)
  }

  async download (document) {
    const waitFn = `document.write('${`
      <div style="padding: 5%; text-align: center; color: #2395F3; font-size: 28px; font-family: arial, helvetica; font-weight: 300">
        Please wait, generating secure download link - this can take a few seconds.
      </div>
    `}')`
    const newTab = window.open('javascript:' + waitFn, '_blank')
    const { DownloadLink } = await this.rootStore.api.documents.get(document.id)
    newTab.location.assign(DownloadLink)
  }

  async fetchDocuments () {
    this.toggleLoading(true)
    try {
      const mapped = await this.rootStore.retryOnNetworkFailure(() => this.rootStore.api.documents.find())
        .retryMessage('Document system is unreachable, retrying...')
        .recoveredMessage('Never mind, we\'re back!')
        .networkIssueMessage('Could not load documents due to a network issue.')
      if (mapped === false) {
        return
      }

      runInAction(() => {
        const updated = this.documents.set(mapped)
        const userIds = updated.map(x => x.uploadedBy && x.uploadedBy.id).filter(x => x)
        this.rootStore.userStore.refreshProfiles(userIds)
      })
    } finally {
      this.toggleLoading(false)
    }
  }

  async upload (document) {
    const result = await this.rootStore.api.documents.upload({
      file: document.fileObject,
      job: document.jobId,
      meta: {
        notes: document.description,
        category: document.category,
        type: document.type,
        uploader: document.uploadedBy.id,
        year: document.year,
        name: document.label
      },
      progress: ({ loaded, total }) => {
        if (total) {
          runInAction(() => {
            document.uploadProgress = loaded / total * 100
          })
        }
      }
    }).catch(action((err) => {
      console.error('Upload failed', err)
      this.rootStore.flashMessageStore.create({
        message: 'Upload failed: ' + err.message,
        type: FlashMessageTypes.ERROR
      })
      document.editor.startEditing()
      return false
    }))

    runInAction(() => {
      // When upload completes, remove from staging and add to collection.
      this.stagedDocuments.remove(document)
      document.set(result, { parse: true })
      document.set({
        uploadProgress: null,
        fileObject: null
      })

      this.documents.add(document)
    })
  }

  @action toggleLoading (loading) {
    this.loading = loading
  }
}

export default DocumentStore



// WEBPACK FOOTER //
// ./src/client/store/DocumentStore.js